import type { PathCommand } from '@/lib/elements/types/path-command'
import {
  innerRatioFromInsetPercent,
  insetPercentFromInner,
  starShapeTypeFromInner,
} from '@/lib/object-effects/sides'
import { type AnyRecord, asRecord, finiteNumber } from './tolerant-read'

export const SHAPE_TYPES = [
  'square',
  'circle',
  'triangle',
  'hexagon',
  'pentagon',
  'star',
  'heart',
  'starone',
  'startwo',
  'path',
] as const

export function parseEditorShapeTypeFromRecord(
  editorShape: AnyRecord | null | undefined
): (typeof SHAPE_TYPES)[number] | null {
  if (!editorShape) return null
  const raw = editorShape.type ?? editorShape.Type ?? ''
  const s = typeof raw === 'string' ? raw.toLowerCase() : ''

  return SHAPE_TYPES.includes(s as (typeof SHAPE_TYPES)[number])
    ? (s as (typeof SHAPE_TYPES)[number])
    : null
}

/**
 * `editorShape` is frontend-only; it is not in protobuf. S3-decoded `decodedData` often omits it,
 * but the checkpoint element wrapper may still carry `editorShape` from the API.
 * Returns `null` when no valid type is found in either location.
 */
export function getEditorShapeTypeOrNull(
  decodedData: AnyRecord,
  element?: AnyRecord | null
): (typeof SHAPE_TYPES)[number] | null {
  const fromDecoded = parseEditorShapeTypeFromRecord(
    asRecord(decodedData.editorShape) ?? asRecord(decodedData.EditorShape)
  )

  if (fromDecoded) return fromDecoded

  if (element) {
    const fromElement = parseEditorShapeTypeFromRecord(
      asRecord(element.editorShape) ?? asRecord(element.EditorShape)
    )

    if (fromElement) return fromElement
  }

  return null
}

export function getEditorShapeInsetOrNull(
  decodedData: AnyRecord,
  element?: AnyRecord | null
): number | null {
  const fromDecoded = asRecord(decodedData.editorShape) ?? asRecord(decodedData.EditorShape)
  const insetDecoded = finiteNumber(fromDecoded?.inset ?? fromDecoded?.Inset)

  if (insetDecoded != null) return insetDecoded

  if (element) {
    const fromElement = asRecord(element.editorShape) ?? asRecord(element.EditorShape)
    const insetElement = finiteNumber(fromElement?.inset ?? fromElement?.Inset)

    if (insetElement != null) return insetElement
  }

  return null
}

/**
 * Resolve the shape type for a `RegularPolygonElement` (kindId 10) using the protobuf-native
 * `inner` ratio (uniquely identifies each star variant) and `sides` count, so stars/polygons
 * round-trip correctly even when `editorShape` is dropped from the wire format.
 */
export function inferRegularPolygonShapeType(decodedData: AnyRecord): (typeof SHAPE_TYPES)[number] {
  const innerNum = finiteNumber(decodedData.inner ?? decodedData.Inner)
  // `inner` may be stored as either the legacy 0..1 ratio or the newer 0..100 inset percent
  // (see `insetPercentFromInner`); normalize to a ratio before matching a star variant, or a
  // default-inset star (`inner: "50"`) is mistaken for a plain polygon and never matches.
  const star =
    innerNum != null
      ? starShapeTypeFromInner(innerRatioFromInsetPercent(insetPercentFromInner(innerNum)))
      : null

  if (star) return star

  const sides = finiteNumber(decodedData.sides ?? decodedData.Sides)

  if (sides === 3) return 'triangle'
  if (sides === 5) return 'pentagon'
  if (sides === 6) return 'hexagon'

  return 'hexagon'
}

/**
 * Best-effort fallback when `editorShape.type` is missing for kindId 2.
 * We only need strong detection for curve-based shapes so circle/heart keep
 * their behavior (rounding/sides disabled) after reload.
 */
export function inferIrregularPolygonShapeType(
  pathCommands: PathCommand[] | null
): (typeof SHAPE_TYPES)[number] {
  if (!pathCommands || pathCommands.length === 0) return 'square'

  // A real circle/heart preset is always a single contour (one moveTo). A compound path —
  // multiple moveTo's, e.g. a converted-from-text shape with several glyphs, or a glyph with a
  // hole — is never one of these presets, however many curves it happens to contain: a curve
  // count alone can't distinguish "a heart" from "an 'S' glyph plus three other letters." `'path'`
  // is the correct label here, not just a safe one: it's exactly what the SAME shape's `type` is
  // in memory before it's ever saved (`buildShapeFromTextOutline`/Edit Path conversion both set
  // `type: 'path'`), and it's what actually disables Object Effects controls (rounding, sides)
  // that assume a preset's own parametric geometry — labeling it `'square'` left those controls
  // wrongly enabled after a reload, exactly the outcome this function exists to avoid.
  const moveToCount = pathCommands.filter((c) => 'moveTo' in c).length

  if (moveToCount > 1) return 'path'

  const curveCount = pathCommands.filter((c) => 'curveTo' in c).length

  if (curveCount > 0) {
    // Circle is generated with 4 cubic segments; heart has many more.
    return curveCount >= 10 ? 'heart' : 'circle'
  }

  // For line-only irregular shapes, keep current conservative fallback.
  return 'square'
}
